import React from "react";

import { Card, Heading, Icon, Layout, Tabs } from "@swa-ui/core";
import { HotelBookingForm } from "@swa-ui/hotel";
import i18n from "@swa-ui/locale";
import { usePlacement } from "@swa-ui/placements";
import { CarBookingForm, FlightBookingForm } from "@swa-ui/shopping";
import { classNames } from "@swa-ui/string";
import {
  CruiseBookingContainer,
  VacationsBookingFormSlim,
  VacationsBookingFormWide,
} from "@swa-ui/vacations";

import styles from "./BookingWidgets.module.scss";

const WIDGETS_CONFIGURATION = {
  "booking-widget-cars": {
    icon: "Car",
    regular: CarBookingForm,
    slim: CarBookingForm,
  },
  "booking-widget-cruise": {
    icon: "Cruise",
    regular: CruiseBookingContainer,
    slim: CruiseBookingContainer,
  },
  "booking-widget-flight": {
    icon: "Flight",
    regular: FlightBookingForm,
    slim: FlightBookingForm,
  },
  "booking-widget-hotel": {
    icon: "Hotel",
    regular: HotelBookingForm,
    slim: HotelBookingForm,
  },
  "booking-widget-swav-ojt": {
    icon: "Vacation",
    regular: VacationsBookingFormWide,
    slim: VacationsBookingFormSlim,
  },
};

/**
 * This component renders various booking/search widgets including Flight, Vacations, Cars, Cruises and Hotels.
 * The widgets layout is fully configurable by placement/bootstrap data and can be rendered in tabbed and non tabbed layouts.
 */
export const BookingWidgets = () => {
  const widgetParameters = usePlacement("bookingWidget")?.placement?.swaTabbedWidgets;
  const isSlimLayout = widgetParameters?.[0]?.width === "slim";

  return (
    widgetParameters && (
      <Layout sides center>
        <div className={getContainerClass()}>{renderContent()}</div>
      </Layout>
    )
  );

  function renderContent() {
    return widgetParameters.length > 1 ? renderTabbedLayout() : renderNonTabbedLayout();
  }

  function renderTabbedLayout() {
    return <Tabs {...getTabsProps()} />;
  }

  function renderNonTabbedLayout() {
    const widgetId = widgetParameters?.[0]?.id;

    return WIDGETS_CONFIGURATION[widgetId] ? (
      <Card {...getCardProps()}>
        {renderCardHeading(widgetParameters[0])}
        <div className={styles.widget}>{getWidgetComponent(widgetId)}</div>
      </Card>
    ) : null;
  }

  function renderCardHeading(widgetConfig) {
    return (
      <div className={styles.cardHeader}>
        <Heading headingLevel={2} styleLevel={3}>
          <div className={styles.heading}>{i18n("BookingWidgets__HEADING")}</div>
        </Heading>
        <div className={styles.headerIconContainer}>
          <Icon {...getIconProps(widgetConfig)} />
          <div className={styles.headingDescription}>{widgetConfig.heading}</div>
        </div>
      </div>
    );
  }

  function getContainerClass() {
    return classNames({
      [styles.slim]: isSlimLayout,
    });
  }

  function getCardProps() {
    return {
      fullHeight: true,
      fullHeightContent: true,
      fullWidthContent: true,
    };
  }

  function getTabsProps() {
    return {
      "aria-label-next": i18n("BookingWidgets__BUTTON_GROUP_ARIA_NEXT"),
      "aria-label-previous": i18n("BookingWidgets__BUTTON_GROUP_ARIA_PREVIOUS"),
      fullWidth: true,
      items: getTabItems(),
    };
  }

  function getTabItems() {
    const availableWidgets = widgetParameters.filter(
      (widgetParameter) => WIDGETS_CONFIGURATION[widgetParameter.id]
    );

    return availableWidgets.map((widgetParameter) => {
      const { heading, id } = widgetParameter;

      return {
        content: getWidgetComponent(id),
        label: heading,
      };
    });
  }

  function getIconProps(widgetConfig) {
    return {
      color: "booking-widget-heading-icon-color",
      name: widgetConfig.icon || WIDGETS_CONFIGURATION[widgetConfig.id].icon,
      size: "size26",
    };
  }

  function getWidgetComponent(widgetId) {
    const Component = getWidgetComponentType(widgetId);

    return Component ? <Component {...getWidgetComponentProps(widgetId)} /> : null;
  }

  function getWidgetComponentProps(widgetId) {
    const widgetConfig = widgetParameters.find(
      (widgetParameter) => widgetParameter.id === widgetId
    );

    return {
      defaultFormDataOverride: widgetConfig.props || {},
    };
  }

  function getWidgetComponentType(widgetId) {
    return isSlimLayout
      ? WIDGETS_CONFIGURATION[widgetId].slim
      : WIDGETS_CONFIGURATION[widgetId].regular;
  }
};
